'use client';

import classNames from 'classnames';
import { MedicareApiPlan } from 'types/api';
import { useAppDispatch, useAppSelector } from 'lib/hooks';
import { removeFromCart, addToCart, unsavePlan } from 'slices/cartSlice';
import { toggleCompare } from 'slices/comparisonSlice';
import { selectIsInComparison, selectComparisonFull } from 'slices/selectors';
import AlexPlanScore from './recommendation/AlexPlanScore';
import PlanCardDetails from './PlanCardDetails';
import AlexRecommendedBadge from './plan/AlexRecommendedBadge';
import PlanTypeBanner from './plan/PlanTypeBanner';
import PlanHeader from './plan/PlanHeader';
import PlanActions from './plan/PlanActions';
import { getBrowseUserId, getCachedLocation } from 'utils/browseSession';
import { getPlanDetailsUrl } from 'utils/planFormatting';
import { Location, selectCurrentLocation } from 'slices/locationSlice';
import { selectUser } from 'slices/userSlice';
import styles from './PlanCard.module.scss';
import { useCallback, useTransition } from 'react';
import type { PlanCardContext } from './plan/planCardTypes';
import { useSelectedPlanPersistence } from 'hooks/useSelectedPlanPersistence';
import { usePlanInteractionState } from 'hooks/usePlanInteractionState';
import { capturePlanAddedToCart } from 'lib/analytics';
import { selectAvailabilityState } from 'slices/availabilitySlice';

interface PlanCardProps {
  plan: MedicareApiPlan;
  context?: PlanCardContext;
  location?: Location;
  // For browse context
  recommendations?: MedicareApiPlan[];
  userId?: string;
  isInRecommendedTab?: boolean;
  enrollmentDisabled?: boolean;
}

export default function PlanCard({
  plan,
  context = 'browse',
  location,
  userId,
  isInRecommendedTab = false,
  enrollmentDisabled = false,
}: PlanCardProps) {
  // For comparison tray, set MA and MAPD plans to 'mapd'
  const planType = plan.type === 'ma' ? 'mapd' : plan.type;
  const dispatch = useAppDispatch();
  const storeLocation = useAppSelector(selectCurrentLocation);
  const storeUser = useAppSelector(selectUser);
  const { selectedPlanYear } = useAppSelector(selectAvailabilityState);
  const planYear = plan.planYear ?? selectedPlanYear;
  const { persistPlan, removePlan } = useSelectedPlanPersistence();
  const [isPending, startTransition] = useTransition();
  const { isInCart, isRecommended } = usePlanInteractionState(plan);

  const resolvePlanDetailsUrl = useCallback(() => {
    const effectiveLocation =
      [location, storeLocation].find((loc) => loc?.zipcode) ??
      getCachedLocation();
    const effectiveUserId = userId || storeUser.id || getBrowseUserId();

    return getPlanDetailsUrl(
      plan,
      effectiveUserId,
      effectiveLocation,
      planYear
    );
  }, [location, plan, planYear, storeLocation, storeUser.id, userId]);

  const isInComparison = useAppSelector((state) =>
    selectIsInComparison(state, { id: plan.id, planYear }, planType)
  );
  const isComparisonFull = useAppSelector((state) =>
    selectComparisonFull(state, planType, planYear)
  );

  const handleDelete = () => {
    startTransition(async () => {
      if (context === 'saved-plans') {
        await removePlan(plan.id, 'saved', planYear);
        dispatch(unsavePlan(plan));
      } else {
        // browse toggle-off and cart-page delete both drop cart membership;
        // selected_plans must be updated or the plan reappears after reload.
        await removePlan(plan.id, 'cart', planYear);
        dispatch(removeFromCart(plan));
      }
    });
  };

  const handleAddToCart = () => {
    startTransition(async () => {
      if (context === 'browse' && isInCart) {
        await removePlan(plan.id, 'cart', planYear);
        dispatch(removeFromCart(plan));
      } else {
        // Saved Plans is login-gated and its cart button only updates Redux.
        // A refresh reloads selected_plans, so the add has to be written first.
        if (context === 'browse' || context === 'saved-plans') {
          await persistPlan(plan, 'cart');
        }
        dispatch(addToCart(plan));
        capturePlanAddedToCart({
          plan_type: plan.type,
          plan_id: plan.id,
          context,
        });
      }
    });
  };

  const handleCompareToggle = () => {
    dispatch(toggleCompare({ plan, planType }));
  };

  const isBrowseContext = context === 'browse';
  const isSavedPlansContext = context === 'saved-plans';
  const isCartContext = context === 'cart';
  const planTypeClass = {
    ma: styles.maCard,
    mapd: styles.maCard,
    gap: styles.gapCard,
    pdp: styles.pdpCard,
  }[plan.type];

  const planCardClass = classNames(
    styles.planCard,
    {
      [styles.planCardSelected]: isInComparison && isBrowseContext,
      [styles.cartContext]: isCartContext,
    },
    planTypeClass
  );

  const planColor =
    plan.type === 'gap'
      ? 'var(--blue-800)'
      : plan.type === 'pdp'
      ? 'var(--peach-500)'
      : 'var(--navy-400)';

  return (
    <article className={styles.planCardWrapper}>
      {isRecommended && <AlexRecommendedBadge />}
      <div
        id={isCartContext ? `cart-plan-${plan.id}` : undefined}
        className={planCardClass}
      >
        {isInComparison && isBrowseContext && (
          <span className="visually-hidden">Selected for comparison</span>
        )}
        <div className={styles.colorBar}></div>
        <PlanTypeBanner plan={plan} planColor={planColor} />
        <div className={styles.planCardContent}>
          <PlanHeader plan={plan} />
          <div className={styles.planDetails}>
            <PlanCardDetails plan={plan} />
            <AlexPlanScore plan={plan} context={context} />
          </div>
        </div>
        <PlanActions
          plan={plan}
          context={context}
          detailsPageUrl={resolvePlanDetailsUrl()}
          isInCart={isInCart}
          isSavedPlansContext={isSavedPlansContext}
          isInRecommendedTab={isInRecommendedTab}
          onAddToCart={handleAddToCart}
          onDelete={handleDelete}
          isPending={isPending}
          enrollmentDisabled={enrollmentDisabled}
          isInComparison={isInComparison}
          isComparisonFull={isComparisonFull}
          onCompareToggle={handleCompareToggle}
        />
      </div>
    </article>
  );
}
